import { FunnelPerStageStatistics } from '@tassomutuo-backoffice/types';
import { Box, Switch, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import React, { useState } from 'react';

interface CustomStageLabelProps {
  x?: number;
  y?: number;
  stage: string;
}

interface CustomValueLabelProps {
  x?: number;
  y?: number;
  width?: number;
  value: number | string;
}

const StageLabel = ({ x = 0, y = 0, stage }: CustomStageLabelProps) => {
  return (
    <text x={x} y={y - 6} fill="#56565d" fontSize={14} textAnchor="start">
      {labelMap[stage as keyof typeof labelMap]}
    </text>
  );
};

const ValueLabel = ({
  x = 0,
  y = 0,
  width = 0,
  value,
}: CustomValueLabelProps) => {
  if (!value || value === '0%') return <></>;

  return (
    <text
      x={x + width + 8}
      y={y + 14}
      fill="#56565d"
      fontSize={14}
      textAnchor="start"
    >
      {value}
    </text>
  );
};

const labelMap = {
  totalReceivedLeads: 'Lead ricevute',
  confirmedAppointments: 'Appuntamento confermate',
  meetingDone: 'Incontrate',
  inDevelopment: 'In gestione',
  advancedStage: 'Fase avanzata',
  concludedNotarialActs: 'Vinte',
  dropped: 'Dropped',
};

interface IRow {
  id: string;
  stage: string;
  number: number;
  percentage: string | number;
  percentageWithPreviousStage: string | number;
  benchmarkNumber: number | null;
  benchmarkPercentage: string | number | null;
  benchmarkPercentageWithPreviousStage: string | number;
}

const getFormattedData = (
  funnelPerStageStatistics: FunnelPerStageStatistics,
  benchmarkFunnelPerStageStatistics?: FunnelPerStageStatistics
) => {
  const rows: IRow[] = Object.entries(funnelPerStageStatistics)
    .filter(([stage]) => stage !== 'confirmedAppointments' && stage !== 'rawStageCount')
    .map(([stage, number]) => {
      const benchmarkNumber =
        benchmarkFunnelPerStageStatistics?.[stage as keyof typeof labelMap] ||
        null;

      const benchmarkPercentage =
        !benchmarkNumber || !benchmarkFunnelPerStageStatistics
          ? null
          : stage === 'totalReceivedLeads'
          ? 100
          : benchmarkFunnelPerStageStatistics.totalReceivedLeads > 0
          ? Number(
              (
                (benchmarkNumber /
                  benchmarkFunnelPerStageStatistics.totalReceivedLeads) *
                100
              ).toFixed(2)
            )
          : 0;

      const calculatePercentage = () => {
        if (stage === 'totalReceivedLeads') {
          return 100;
        }
        if (
          !funnelPerStageStatistics.totalReceivedLeads ||
          funnelPerStageStatistics.totalReceivedLeads === 0
        ) {
          return 0;
        }
        return Number(
          (
            (number / funnelPerStageStatistics.totalReceivedLeads) *
            100
          ).toFixed(2)
        );
      };

      return {
        id: stage,
        stage: labelMap[stage as keyof typeof labelMap],
        number,
        benchmarkNumber,
        percentage: calculatePercentage(),
        benchmarkPercentage,
        percentageWithPreviousStage: '',
        benchmarkPercentageWithPreviousStage: '',
      };
    });

  for (let i = 1; i < rows.length; i++) {
    const currentNumber = rows[i].number;
    const previousNumber = rows[i - 1].number;
    const currentStage = rows[i].id;

    const benchCurrentNumber = rows[i].benchmarkNumber;
    const benchPreviousNumber = rows[i - 1].benchmarkNumber;

    if (currentStage !== 'dropped') {
      rows[i].percentageWithPreviousStage = previousNumber
        ? Number(((currentNumber / previousNumber) * 100).toFixed(2))
        : '';

      rows[i].benchmarkPercentageWithPreviousStage =
        benchPreviousNumber && benchCurrentNumber
          ? Number(
              ((benchCurrentNumber / benchPreviousNumber) * 100).toFixed(2)
            )
          : '';
    }
  }

  return rows;
};

interface IProps {
  funnelPerStageStatistics: FunnelPerStageStatistics;
  benchmarkFunnelPerStageStatistics?: FunnelPerStageStatistics;
  isPrinting?: boolean;
}

const DashboardFunnelPerStage = ({
  funnelPerStageStatistics,
  benchmarkFunnelPerStageStatistics,
  isPrinting,
}: IProps) => {
  const [graphDataToShow, setGraphDataToShow] = useState<
    'number' | 'percentage'
  >('percentage');

  const rows = getFormattedData(
    funnelPerStageStatistics,
    benchmarkFunnelPerStageStatistics
  );

  const onChangeSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    // e.preventDefault();
    setGraphDataToShow((val) => (val === 'number' ? 'percentage' : 'number'));
  };

  const receivedLeads = rows.find((r) => r.id === 'totalReceivedLeads')?.number;

  return (
    <Box
      sx={{
        backgroundColor: '#FAFAFA',
        padding: 4,
        mt: 6,
        borderRadius: '16px',
        border: '1px solid gainsboro',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h4" mb={1} fontWeight="bold">
            Funnel per stage
          </Typography>
          <Typography variant="h6" mb={4}>
            Cambia il tipo di visualizzazione dal pulsante a destra
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography fontWeight="bold" fontSize={18}>
            %
          </Typography>
          <Switch
            checked={graphDataToShow === 'number'}
            onChange={onChangeSwitch}
          />
          <Typography fontWeight="bold" fontSize={18}>
            N
          </Typography>
        </Box>
      </Box>

      {/* GRAFICO */}
      <Box height={400} mb={0} sx={{ minWidth: isPrinting ? 1100 : undefined }}>
        <ResponsiveContainer width="90%" height="100%">
          <BarChart
            data={rows}
            layout="vertical"
            barCategoryGap={15}
            margin={{ right: 150 }}
          >
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="stage" hide />
            {/*   <Tooltip formatter={(value: number) => `${value}`} />
           <Legend />*/}
            <Bar
              barSize={15}
              dataKey={graphDataToShow}
              name=""
              fill="rgba(16, 60, 85, 1)"
              label={(props) => (
                <>
                  <StageLabel {...props} stage={props.id} />
                  <ValueLabel
                    {...props}
                    value={
                      props.id === 'totalReceivedLeads'
                        ? receivedLeads
                        : graphDataToShow === 'percentage'
                        ? `${props.value}%`
                        : props.value
                    }
                  />
                </>
              )}
            />
            {graphDataToShow === 'percentage' && (
              <Bar
                barSize={15}
                dataKey="benchmarkPercentage"
                name=""
                label={(props) => (
                  <ValueLabel
                    {...props}
                    value={
                      props.id === 'totalReceivedLeads'
                        ? null
                        : graphDataToShow === 'percentage'
                        ? `${props.value || 0}%`
                        : props.value
                    }
                  />
                )}
              >
                {rows.map((entry, index) => (
                  <Cell
                    key={`cell-benchmark-${index}`}
                    fill={
                      entry.id === 'totalReceivedLeads'
                        ? 'transparent'
                        : 'rgba(59, 130, 246, 1)'
                    }
                  />
                ))}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* TABELLA */}
      {/*<DataGrid
        rows={rows}
        columns={tableColumns}
        hideFooter
        hideFooterPagination
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        autoHeight
      />*/}
    </Box>
  );
};

export default DashboardFunnelPerStage;
